import React, { useEffect, useMemo, useRef } from "react";

export default function Stepper({ stepIndex, steps }) {
  const total = steps.length;
  const scrollerRef = useRef(null);

  // IDs for scroll-to-current
  const itemIds = useMemo(
    () => steps.map((s, idx) => `step-${s.id || idx}`),
    [steps]
  );

  // Auto-scroll to current step on mobile
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;

    const activeId = itemIds[stepIndex];
    const node = activeId ? document.getElementById(activeId) : null;
    if (!node) return;

    // only auto-scroll when in mobile layout (scroll container visible)
    // safe to call anyway
    node.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  }, [stepIndex, itemIds]);

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-3 sm:p-4 shadow-sm">
      {/* ✅ Mobile: horizontal scroller */}
      <div
        ref={scrollerRef}
        className="
          -mx-3 px-3
          flex gap-3 overflow-x-auto pb-2
          snap-x snap-mandatory
          [scrollbar-width:none] [-ms-overflow-style:none]
          sm:hidden
        "
      >
        {/* hide scrollbar (webkit) */}
        <style>{`
          .hide-scrollbar::-webkit-scrollbar { display: none; }
        `}</style>

        <div className="hide-scrollbar flex gap-3">
          {steps.map((s, idx) => {
            const n = idx + 1;
            const active = idx <= stepIndex;
            const isCurrent = idx === stepIndex;

            return (
              <div
                id={itemIds[idx]}
                key={s.id || idx}
                className="snap-center shrink-0 w-[260px] rounded-2xl border border-gray-200 bg-white p-4"
              >
                <div className="flex items-start gap-3">
                  <div
                    className={[
                      "flex h-9 w-9 items-center justify-center rounded-xl text-sm font-bold shrink-0",
                      active
                        ? "bg-[#0b2b40] text-white shadow-sm shadow-emerald-600/25"
                        : "bg-slate-100 text-slate-500",
                      isCurrent ? "ring-4 ring-emerald-600/15" : "",
                    ].join(" ")}
                  >
                    {n}
                  </div>

                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-900">{s.title}</div>
                    <div className="mt-0.5 text-xs text-slate-500">{s.subtitle}</div>

                    {/* little progress text */}
                    <div className="mt-3 text-[11px] font-semibold text-slate-500">
                      Schritt {n} von {total}
                    </div>

                    {/* tiny progress bar */}
                    <div className="mt-2 h-[2px] w-full rounded-full bg-gray-200">
                      <div
                        className="h-[2px] rounded-full bg-[#0b2b40] transition-all"
                        style={{ width: active ? "100%" : "0%" }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ✅ Desktop: your original layout (unchanged) */}
      <div className="hidden sm:flex items-center justify-between gap-3">
        {steps.map((s, idx) => {
          const n = idx + 1;
          const active = idx <= stepIndex;
          const isCurrent = idx === stepIndex;

          return (
            <div key={s.id || idx} className="flex flex-1 items-center gap-3">
              <div
                className={[
                  "flex h-9 w-9 items-center justify-center rounded-xl text-sm font-bold",
                  active
                    ? "bg-[#0b2b40] text-white shadow-sm shadow-emerald-600/25"
                    : "bg-slate-100 text-slate-500",
                  isCurrent ? "ring-4 ring-emerald-600/15" : "",
                ].join(" ")}
              >
                {n}
              </div>

              <div className="min-w-0">
                <div className="text-sm font-semibold text-slate-900">{s.title}</div>
                <div className="text-xs text-slate-500">{s.subtitle}</div>
              </div>

              {idx !== total - 1 && (
                <div className="hidden md:block flex-1 px-2">
                  <div className="h-[2px] w-full rounded-full bg-gray-200">
                    <div
                      className="h-[2px] rounded-full bg-[#0b2b40] transition-all"
                      style={{ width: stepIndex > idx ? "100%" : "0%" }}
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
